/**
 * Endpoints de *Configuración → Empresa* (`/api/empresa*`).
 *
 * La pantalla la usa el grupo `configuration`; cambiar el plan contratado queda
 * reservado al administrador principal (la API responde 403).
 *
 * Las imágenes viajan en binario (`POST /api/empresa/imagen/:campo`) porque el
 * cliente HTTP envía JSON; y se muestran con `urlImagenEmpresa()`, que apunta a
 * la única ruta pública del módulo (los logos también salen en el login).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { env } from '@/config/env';
import { apiClient } from '@/lib/http/api-client';
import { getSessionProvider } from '@/lib/http/session-provider';

import type { CampoImagenEmpresa, DocumentoConsultado, EmpresaPayload, EmpresaRespuesta, ImagenEmpresa } from './empresa.types';

export const empresaKeys = {
  all: ['empresa'] as const,
  detalle: ['empresa', 'detalle'] as const,
  modulosPlan: (id: number) => ['empresa', 'plan-modulos', id] as const,
} as const;

export const empresaApi = {
  obtener: (signal?: AbortSignal): Promise<EmpresaRespuesta> =>
    apiClient.get<EmpresaRespuesta>('/api/empresa', { signal }),

  guardar: (
    payload: EmpresaPayload,
  ): Promise<EmpresaRespuesta & { mensaje: string }> =>
    apiClient.put<EmpresaRespuesta & { mensaje: string }>('/api/empresa', { body: payload }),

  cambiarPlan: (
    payload: { plan_id: number; tipo: 'mensual' | 'anual' },
  ): Promise<{ mensaje: string }> =>
    apiClient.patch<{ mensaje: string }>('/api/empresa/plan', { body: payload }),

  modulosDelPlan: (
    id: number,
    signal?: AbortSignal,
  ): Promise<ModulosDelPlanRespuesta> =>
    apiClient.get<ModulosDelPlanRespuesta>(`/api/empresa/plan/${id}/modulos`, { signal }),

  /** Botón «Buscar en SUNAT» del RUC (no escribe nada). */
  consultarRuc: (
    numero: string,
    signal?: AbortSignal,
  ): Promise<{ documento: DocumentoConsultado }> =>
    apiClient.get<{ documento: DocumentoConsultado }>(
      `/api/empresa/consulta-ruc?numero=${encodeURIComponent(numero)}`,
      { signal },
    ),
} as const;

/** Módulos que incluye un plan (vista previa antes de contratarlo). */
export interface ModulosDelPlanRespuesta {
  total: number;
  modulos: { id: number; codigo: string; nombre: string; categoria: string; descripcion: string }[];
  categorias: Record<string, number>;
}

/** URL de una imagen del sistema (ruta pública, sin token). */
export function urlImagenEmpresa(campo: CampoImagenEmpresa, version: string | null): string {
  const sufijo = version ? `?v=${encodeURIComponent(version)}` : '';
  return `${env.apiUrl}/api/empresa/imagen/${campo}${sufijo}`;
}

/**
 * Sube una imagen del sistema. Se hace con `fetch` directo porque el cuerpo es
 * el archivo (no JSON) y hay que enviar el token de la sesión.
 */
export async function subirImagenEmpresa(
  campo: CampoImagenEmpresa,
  archivo: File,
): Promise<ImagenEmpresa> {
  const token = getSessionProvider()?.getAccessToken();
  const respuesta = await fetch(`${env.apiUrl}/api/empresa/imagen/${campo}`, {
    method: 'POST',
    headers: {
      'Content-Type': archivo.type || 'image/png',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: archivo,
  });

  const contenido = (await respuesta.json().catch(() => null)) as
    | { status: boolean; response?: { imagen: ImagenEmpresa }; error?: string }
    | null;

  if (!respuesta.ok || !contenido?.status || !contenido.response) {
    throw new Error(contenido?.error ?? `No se pudo subir la imagen (HTTP ${respuesta.status}).`);
  }

  return contenido.response.imagen;
}

/** Datos completos de la empresa. */
export function useEmpresa(enabled = true) {
  return useQuery({
    queryKey: empresaKeys.detalle,
    queryFn: ({ signal }) => empresaApi.obtener(signal),
    enabled,
    staleTime: 30_000,
  });
}

/** Guarda la configuración y refresca el estado local con lo que devuelve la API. */
export function useGuardarEmpresa() {
  const consulta = useQueryClient();

  return useMutation({
    mutationFn: (payload: EmpresaPayload) => empresaApi.guardar(payload),
    onSuccess: (respuesta) => {
      consulta.setQueryData(empresaKeys.detalle, respuesta);
    },
  });
}

/** Cambia el plan contratado (sólo administrador principal). */
export function useCambiarPlan() {
  const consulta = useQueryClient();

  return useMutation({
    mutationFn: (payload: { plan_id: number; tipo: 'mensual' | 'anual' }) =>
      empresaApi.cambiarPlan(payload),
    onSuccess: () => consulta.invalidateQueries({ queryKey: empresaKeys.all }),
  });
}

/** Sube una imagen del sistema. */
export function useSubirImagenEmpresa() {
  const consulta = useQueryClient();

  return useMutation({
    mutationFn: ({ campo, archivo }: { campo: CampoImagenEmpresa; archivo: File }) =>
      subirImagenEmpresa(campo, archivo),
    onSuccess: () => consulta.invalidateQueries({ queryKey: empresaKeys.detalle }),
  });
}

/**
 * Consulta el RUC en SUNAT para rellenar la ficha de la empresa.
 *
 * Es una mutación y no una consulta: no lleva caché (cada búsqueda es un aviso al
 * servicio externo) y sólo se dispara al pulsar la lupa.
 */
export function useConsultarRuc() {
  return useMutation({
    mutationFn: (numero: string) => empresaApi.consultarRuc(numero),
  });
}
